import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from "react";
import {AppState} from "react-native";
import {useMutation, useQueryClient, useSuspenseQuery} from "@tanstack/react-query";
import {useFocusEffect} from "expo-router";
import type {Channel, ChannelMember, ChatClient, Message, PresenceUpdate, TypingUpdate} from "@emb-chat/react-native-sdk";
import * as Sentry from "@sentry/react-native";
import {type ChatConversation, getChatDms, getChatGroups, startDm} from "@/data/chat-api";
import {
    getChatClient,
    getChatClientGeneration,
    isChatAuthError,
    refreshChatClient,
    subscribeToChatClient,
    withChatClient
} from "@/lib/chat-client";
import {pb} from "@/lib/pocketbase";

export const chatKeys = {
    groups: (orgId: string) => ["chat", "groups", orgId] as const,
    // DMs are per-organization: the org must be in the key, or switching orgs would serve
    // another org's conversations from cache.
    dms: (orgId: string) => ["chat", "dms", orgId] as const
};

/**
 * Bumps whenever the shared ChatClient is replaced. A Channel handle and its socket listeners
 * belong to the client that created them, so a consumer holding one has to tear it down and
 * rebuild against the new client — which is why this sits in the effect deps below, and why a
 * refresh needs no separate retry path: re-running the effect IS the retry.
 */
function useChatClientGeneration(): number {
    // The third argument matters: the web build is pre-rendered (npm run web:export), which needs
    // a server snapshot.
    return useSyncExternalStore(subscribeToChatClient, getChatClientGeneration, getChatClientGeneration);
}

/**
 * Socket emits on a channel whose client has been retired throw "socket not connected": the swap
 * disconnects the old client before React gets to run this effect's cleanup. Every call guarded
 * here is a fire-and-forget hint — typing dots, read position — so a lost one costs nothing, while
 * letting one escape a cleanup would take the screen down.
 */
function quietly(emit: () => void): void {
    try {
        emit();
    } catch {
        // the handle belongs to a client that has already been replaced
    }
}

export function useChatGroups(orgId: string) {
    return useSuspenseQuery({
        queryKey: chatKeys.groups(orgId),
        queryFn: () => getChatGroups(orgId),
        staleTime: 1000 * 60
    });
}

/** The org's group conversations plus the caller's DMs in that org, merged for the chat list. */
export function useChatConversations(orgId: string): ChatConversation[] {
    const groups = useSuspenseQuery({
        queryKey: chatKeys.groups(orgId),
        queryFn: () => getChatGroups(orgId),
        staleTime: 1000 * 60
    });
    const dms = useSuspenseQuery({
        queryKey: chatKeys.dms(orgId),
        queryFn: () => getChatDms(orgId),
        staleTime: 1000 * 60
    });
    return useMemo(
        () => [
            ...groups.data.map((g): ChatConversation => ({kind: "group", channelId: g.channelId, name: g.name})),
            ...dms.data.map(
                (d): ChatConversation => ({kind: "dm", channelId: d.channelId, name: d.name, otherUserId: d.otherUserId})
            )
        ],
        [groups.data, dms.data]
    );
}

/**
 * Start (or reopen) a DM with another member of this org, refreshing the conversation list so
 * a newly created DM shows up immediately instead of waiting out the list's staleTime.
 */
export function useStartDm(orgId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (otherUserId: string) => startDm(orgId, otherUserId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: chatKeys.dms(orgId)});
        }
    });
}

function sortByCreated(messages: Message[]): Message[] {
    return [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export type PresenceStatus = "online" | "offline";

export interface UseChatChannel {
    messages: Message[];
    loading: boolean;
    hasMore: boolean;
    error: Error | null;
    /** Members of the channel (drives the roster and typing-name lookup). */
    members: ChannelMember[];
    /** user_id → online/offline, kept live from presence events. */
    presence: Record<string, PresenceStatus>;
    /** Other members currently composing (self excluded). */
    typingUserIds: string[];
    sendMessage: (body: string) => Promise<void>;
    loadMore: () => Promise<void>;
    /** Notify the channel the caller is composing (auto-stops after ~3s server-side). */
    startTyping: () => void;
    /** Explicitly stop the typing indicator (call on send/blur). */
    stopTyping: () => void;
    /** Re-run the initial load after a failure. */
    retry: () => void;
}

/**
 * Connects to a channel: loads recent history, appends live `message:new` events,
 * tracks typing and presence, and advances the caller's read position as messages arrive.
 */
export function useChatChannel(channelId: string): UseChatChannel {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [members, setMembers] = useState<ChannelMember[]>([]);
    const [presence, setPresence] = useState<Record<string, PresenceStatus>>({});
    const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
    const generation = useChatClientGeneration();
    const [retryNonce, setRetryNonce] = useState(0);
    // Budget of one token re-mint per successful load — the `_retry` guard from lib/api.ts, in the
    // shape this effect can use. It cannot be keyed on the client generation, because refreshing
    // bumps that generation itself: a token the server rejects no matter how fresh it is (a
    // revoked or unknown signing key) would then mint in a loop forever.
    const refreshAttemptsRef = useRef(0);
    // the channel whose history is already on screen. Tracking the id rather than a bare flag is
    // what separates "swapped client, same conversation" (keep the messages, no spinner) from
    // "different conversation" (spinner, or the previous channel's messages show under the new
    // header until its history lands)
    const loadedChannelRef = useRef<string | null>(null);
    // oldest loaded message id, for backward pagination without re-creating loadMore each render
    const oldestIdRef = useRef<string | undefined>(undefined);
    // the live channel handle, so typing/read helpers don't re-resolve the client
    const channelRef = useRef<Channel | null>(null);
    // last message id we've marked read, to avoid redundant markRead calls
    const lastMarkedRef = useRef<string | undefined>(undefined);
    const myId = pb.authStore.record?.id;

    useEffect(() => {
        oldestIdRef.current = messages[0]?.id;
    }, [messages]);

    useEffect(() => {
        let cancelled = false;
        // set when a failure is being answered by swapping the client: the re-run that the swap
        // triggers owns `loading` and `error` from that point on
        let deferredToRefresh = false;
        // safety net: clear a typing indicator if its stop event never arrives
        const typingTimers: Record<string, ReturnType<typeof setTimeout>> = {};

        const onNew = (msg: Message) => {
            setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : sortByCreated([...prev, msg])));
        };
        const onTyping = (u: TypingUpdate) => {
            if (u.user_id === myId) return;
            setTypingUserIds((prev) => {
                const has = prev.includes(u.user_id);
                if (u.typing) return has ? prev : [...prev, u.user_id];
                return has ? prev.filter((id) => id !== u.user_id) : prev;
            });
            if (typingTimers[u.user_id]) clearTimeout(typingTimers[u.user_id]);
            if (u.typing) {
                typingTimers[u.user_id] = setTimeout(() => {
                    setTypingUserIds((prev) => prev.filter((id) => id !== u.user_id));
                    delete typingTimers[u.user_id];
                }, 6000);
            } else {
                delete typingTimers[u.user_id];
            }
        };
        const onPresence = (p: PresenceUpdate) => {
            if (p.user_id === myId) return;
            setPresence((prev) => ({...prev, [p.user_id]: p.status}));
        };

        setError(null);
        if (loadedChannelRef.current !== channelId) setLoading(true);

        let client: ChatClient | null = null;
        (async () => {
            try {
                // `connected` keeps its non-null narrowing inside the closures below; `client` is
                // the hoisted copy the catch needs to name the client that failed.
                const connected = await getChatClient();
                client = connected;
                if (cancelled) return;
                const channel = connected.channel(channelId);
                channelRef.current = channel;
                channel.on("message:new", onNew);
                channel.on("typing:update", onTyping);
                channel.on("presence:update", onPresence);

                const [page, roster] = await Promise.all([
                    channel.getMessages({limit: 50}),
                    connected.channels.members(channelId)
                ]);
                if (cancelled) return;
                setMessages(sortByCreated(page.messages));
                setHasMore(page.hasMore);
                setMembers(roster);
                loadedChannelRef.current = channelId;
                // the connection works, so a later expiry is allowed its own re-mint
                refreshAttemptsRef.current = 0;

                // seed presence for everyone but the caller; events keep it live afterwards
                const others = roster.filter((m) => m.user_id !== myId);
                const seeded = await Promise.all(
                    others.map(async (m) => {
                        try {
                            const status = await connected.users.presence(m.user_id);
                            return [m.user_id, status.status] as const;
                        } catch {
                            return [m.user_id, "offline"] as const;
                        }
                    })
                );
                if (cancelled) return;
                // Merge the seed *under* any presence:update that landed during the await,
                // so a live event isn't clobbered by a now-stale seeded value.
                setPresence((prev) => ({...Object.fromEntries(seeded), ...prev}));
            } catch (err) {
                if (cancelled) return;
                // Replacing the client bumps the generation, which re-runs this effect against the
                // new one: listeners re-subscribe and the load retries in a single path. A 401 that
                // survives a fresh token is not about the token, so that one goes to the user.
                if (isChatAuthError(err) && refreshAttemptsRef.current < 1) {
                    refreshAttemptsRef.current += 1;
                    deferredToRefresh = true;
                    void refreshChatClient(client ?? undefined).catch(() => undefined);
                    return;
                }
                Sentry.captureException(err);
                setError(err as Error);
            } finally {
                if (!cancelled && !deferredToRefresh) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            const channel = channelRef.current;
            channel?.off("message:new", onNew);
            channel?.off("typing:update", onTyping);
            channel?.off("presence:update", onPresence);
            quietly(() => channel?.stopTyping());
            channelRef.current = null;
            lastMarkedRef.current = undefined;
            Object.values(typingTimers).forEach(clearTimeout);
        };
        // `generation` is not read in the body but must stay: it is what re-runs this effect after
        // the shared client is replaced, rebinding the listeners and refetching history against the
        // new one. Dropping it would leave a swapped-in client with no subscriptions.
    }, [channelId, myId, generation, retryNonce]);

    // Advance the caller's read position to the newest message they're viewing.
    useEffect(() => {
        const latest = messages[messages.length - 1];
        if (!latest || lastMarkedRef.current === latest.id) return;
        lastMarkedRef.current = latest.id;
        quietly(() => channelRef.current?.markRead(latest.id));
    }, [messages]);

    // Coming back from the background is when a spent token bites: the socket was dropped while
    // the app slept, and socket.io will not retry a handshake the gateway refused — so live
    // delivery is gone with nothing on screen to say so. Touching the client on resume rebuilds it
    // if its token is spent, which bumps the generation and re-runs the subscription effect above.
    useEffect(() => {
        const subscription = AppState.addEventListener("change", (state) => {
            if (state === "active") void getChatClient().catch(() => undefined);
        });
        return () => subscription.remove();
    }, []);

    const sendMessage = useCallback(
        async (body: string) => {
            await withChatClient((client) => client.channel(channelId).sendMessage({body, type: "text"}));
        },
        [channelId]
    );

    const loadMore = useCallback(async () => {
        const before = oldestIdRef.current;
        if (!before) return;
        const page = await withChatClient((client) => client.channel(channelId).getMessages({limit: 50, before}));
        setMessages((prev) => sortByCreated([...page.messages, ...prev]));
        setHasMore(page.hasMore);
    }, [channelId]);

    const startTyping = useCallback(() => quietly(() => channelRef.current?.startTyping()), []);
    const stopTyping = useCallback(() => quietly(() => channelRef.current?.stopTyping()), []);
    const retry = useCallback(() => {
        // an explicit retry is the user asking us to try everything again, re-mint included
        refreshAttemptsRef.current = 0;
        setRetryNonce((n) => n + 1);
    }, []);

    return {
        messages,
        loading,
        hasMore,
        error,
        members,
        presence,
        typingUserIds,
        sendMessage,
        loadMore,
        startTyping,
        stopTyping,
        retry
    };
}

/**
 * Per-channel unread flags for the conversation list. A channel is unread when its
 * newest message is from someone else and the caller hasn't read up to it. On focus it
 * recomputes from each member's read position (so returning from a conversation clears
 * its badge) and subscribes to `message:new`, so a message from another session flips the
 * badge live. Listeners detach on blur, so the open conversation owns its own channel handle.
 */
export function useChatUnread(channelIds: string[]): Record<string, boolean> {
    const [unread, setUnread] = useState<Record<string, boolean>>({});
    const key = channelIds.join(",");
    const generation = useChatClientGeneration();

    const refresh = useCallback(() => {
        let cancelled = false;
        const ids = key ? key.split(",") : [];
        const myId = pb.authStore.record?.id;
        let subscriptions: {channel: Channel; onNew: (m: Message) => void}[] = [];

        (async () => {
            try {
                // The batch and the subscriptions share one callback so the listeners bind to
                // whichever client actually served the batch, even if that is a replacement.
                await withChatClient(async (client) => {
                    const entries = await Promise.all(
                        ids.map(async (id) => {
                            try {
                                const [page, roster] = await Promise.all([
                                    client.channel(id).getMessages({limit: 1}),
                                    client.channels.members(id)
                                ]);
                                const latest = page.messages[0];
                                const me = roster.find((m) => m.user_id === myId);
                                // last_read holds the id of the newest message the caller has read;
                                // a newer message from someone else means there's something unseen.
                                const isUnread = !!latest && latest.sender_id !== myId && me?.last_read !== latest.id;
                                return [id, isUnread] as const;
                            } catch (err) {
                                // A 401 is about the client, not this channel: let it out so the
                                // client gets replaced and the whole batch runs again, instead of
                                // quietly reporting "nothing unread" for every conversation.
                                if (isChatAuthError(err)) throw err;
                                return [id, false] as const;
                            }
                        })
                    );
                    if (cancelled) return;
                    setUnread(Object.fromEntries(entries));

                    subscriptions = ids.map((id) => {
                        const channel = client.channel(id);
                        const onNew = (m: Message) => {
                            if (m.sender_id !== myId) setUnread((prev) => ({...prev, [id]: true}));
                        };
                        channel.on("message:new", onNew);
                        return {channel, onNew};
                    });
                });
            } catch {
                // leave prior state on transient failures
            }
        })();

        return () => {
            cancelled = true;
            subscriptions.forEach(({channel, onNew}) => channel.off("message:new", onNew));
        };
    }, [key, generation]);

    useFocusEffect(refresh);
    return unread;
}
